import React from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, Sparkles, Award } from "lucide-react";
import { WaveBackground } from "@/components/effects/WaveBackground";
import { ParticleField } from "@/components/effects/ParticleField";
import { useLanguage } from "@/i18n/LanguageContext";
import { ASSETS } from "@/data/assets";

const ease = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const { t } = useLanguage();

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 120, damping: 18 });
  const gx = useSpring(useTransform(mx, [-0.5, 0.5], [-40, 40]), { stiffness: 80, damping: 20 });
  const gy = useSpring(useTransform(my, [-0.5, 0.5], [-40, 40]), { stiffness: 80, damping: 20 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="home"
      onMouseMove={onMove}
      className="relative flex min-h-screen items-center overflow-hidden pb-16 pt-32"
    >
      <WaveBackground />
      <ParticleField />
      <div className="pointer-events-none absolute inset-0 bg-gradient-hero" />
      {/* mouse-follow glow */}
      <motion.div
        aria-hidden
        style={{ x: gx, y: gy }}
        className="pointer-events-none absolute left-1/2 top-1/3 hidden h-[460px] w-[460px] -translate-x-1/2 rounded-full md:block"
      >
        <div className="h-full w-full rounded-full bg-primary/15 blur-[120px]" />
      </motion.div>

      <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-14 px-6 lg:grid-cols-[1.05fr_0.95fr]">
        {/* Left */}
        <div className="flex flex-col items-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary"
          >
            <Sparkles className="h-4 w-4" />
            {t.hero.badge}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.08, ease }}
            className="font-display text-4xl font-bold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl"
          >
            {t.hero.titlePre}
            <span className="text-gradient">{t.hero.titleHighlight}</span>
            {t.hero.titlePost}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.16, ease }}
            className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg"
          >
            {t.hero.desc}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.24, ease }}
            className="mt-9 flex flex-col gap-3 sm:flex-row"
          >
            <Button variant="hero" size="xl" asChild>
              <a href="#contact">
                {t.hero.primary}
                <ArrowUpRight className="h-5 w-5" />
              </a>
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.32, ease }}
            className="mt-12 grid w-full max-w-md grid-cols-3 gap-x-6 gap-y-6"
          >
            {t.hero.stats.map((s) => (
              <div key={s.label} className="flex flex-col">
                <span className="font-display text-2xl font-bold text-foreground sm:text-3xl">{s.value}</span>
                <span className="mt-1 text-xs leading-snug text-muted-foreground">{s.label}</span>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right — fox */}
        <motion.div
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.2, ease }}
          style={{ perspective: 1200 }}
          className="relative mx-auto flex w-full max-w-md items-center justify-center lg:max-w-none"
        >
          <div className="absolute inset-0 m-auto h-72 w-72 rounded-full bg-primary/30 blur-[90px] animate-glow-pulse" />
          <motion.img
            src={ASSETS.mascotHero}
            alt="LUKI business fox mascot"
            style={{ rotateX: rx, rotateY: ry }}
            className="relative z-10 w-[90%] max-w-md animate-float mask-radial drop-shadow-[0_30px_60px_rgba(255,107,0,0.3)]"
          />

          {/* floating glass chips */}
          <motion.div
            animate={{ y: [0, -14, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            className="glass absolute -left-2 top-10 z-20 hidden items-center gap-2 rounded-2xl px-4 py-3 shadow-card sm:flex"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/15 text-primary">
              <Award className="h-5 w-5" />
            </span>
            <div className="leading-tight">
              <p className="text-sm font-semibold text-foreground">{t.hero.awardSub}</p>
            </div>
          </motion.div>

          <motion.div
            animate={{ y: [0, 16, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="glass absolute -right-1 bottom-12 z-20 hidden items-center gap-2 rounded-2xl px-4 py-3 shadow-card sm:flex"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent/20 text-accent">
              <Sparkles className="h-5 w-5" />
            </span>
            <div className="leading-tight">
              <p className="text-sm font-semibold text-foreground">{t.hero.statValue}</p>
              <p className="text-xs text-muted-foreground">{t.hero.statSub}</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
